import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { logoBase64 } from '../../../../assets/logoBase64';
import type {
  ParteProduccionDetailResponse,
  GrupoProductoTerminado,
  ProductoTerminadoTipoB,
  GrupoMateriaPrima
} from '../types';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable;
    lastAutoTable: {
      finalY: number;
    };
  }
}

interface CalibreGroup {
  codigo: string;
  cantidad: number;
}

// Calibres estándar en orden
const CALIBRES_TALLO_SOLO = ['10-20', '20-30', '30-40', '40-50', '50-60', '60-80', '80-100', '100-120', '120-over'];
const CALIBRES_MEDIA_VALVA = ['10-20', '20-30', '30-40', '40-60', '60-80', '80-100'];

// Constantes de layout
const FOOTER_HEIGHT = 50;

export const parteProduccionPdfService = {
  /**
   * Genera el PDF del Parte de Producción
   */
  generatePdf(data: ParteProduccionDetailResponse): void {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let yPos = margin;

    // ==========================================
    // HEADER - Logo y título
    // ==========================================

    // Logo (izquierda)
    const logoWidth = 30;
    const logoHeight = 20;
    doc.addImage(logoBase64, 'PNG', margin, yPos, logoWidth, logoHeight);

    // Título central
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('PARTE DE PRODUCCION', pageWidth / 2, yPos + 10, { align: 'center' });

    // Empresa (derecha)
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('CPP. ISABEL GOMEZ DE VIZA', pageWidth - margin, yPos + 10, { align: 'right' });

    yPos = margin + 25;

    // ==========================================
    // INFORMACIÓN DEL DERC
    // ==========================================
    const dercInfo = data.derc;
    const especieNombre = dercInfo.especieNombreComun || 'N/A';
    const especieCientifico = dercInfo.especieNombreCientifico || '';

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');

    // Fila 1: Especie
    doc.text('Especie:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    const especieText = especieCientifico
      ? `${especieNombre} (${especieCientifico})`
      : especieNombre;
    doc.text(especieText, margin + 25, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('N° Orden:', pageWidth - 80, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(String(dercInfo.numeroOrden || data.numeroComprobante), pageWidth - 55, yPos);

    yPos += 6;

    // Fila 2: Fecha de extracción y N° de DERC
    doc.setFont('helvetica', 'bold');
    doc.text('Fecha de extracción:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(dercInfo.fechaExtraccion), margin + 40, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('N° de DERC-', pageWidth - 80, yPos);
    doc.setFont('helvetica', 'normal');
    const dercText = dercInfo.numeroDocumento || 'N/A';
    doc.text(dercText, pageWidth - 55, yPos);

    yPos += 6;

    // Fila 3: Asociación y Procedencia
    doc.setFont('helvetica', 'bold');
    doc.text('Asociación:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(dercInfo.asociacion || 'N/A', margin + 25, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('Procedencia:', pageWidth - 80, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(dercInfo.procedencia || 'N/A', pageWidth - 55, yPos);

    yPos += 6;

    // Fila 4: N° Mallas y N° de Manojos
    doc.setFont('helvetica', 'bold');
    doc.text('N° Mallas:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(String(dercInfo.numeroMallas || 0), margin + 25, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('N° de Manojos:', pageWidth / 2 - 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(String(dercInfo.numeroManojos || 0), pageWidth / 2 + 15, yPos);

    yPos += 6;

    // Fila 5: Placa Vehículo DERC (se quitó Tipo)
    doc.setFont('helvetica', 'bold');
    doc.text('Placa Vehículo:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(dercInfo.placaVehiculo || 'N/A', margin + 32, yPos);

    yPos += 10;

    // ==========================================
    // TABLAS DE PRODUCTOS TERMINADOS
    // ==========================================

    // Agrupar productos por presentación/tipo
    const productosAgrupados = agruparProductosPorPresentacion(data.productosTerminados, data.productosTipoB || []);

    // Determinar presentaciones disponibles
    const presentaciones = Object.keys(productosAgrupados);

    // Calcular altura de cada tabla para determinar layout óptimo
    const tableInfos = presentaciones.map(presentacion => {
      const calibresBase = presentacion.toLowerCase().includes('media valva')
        ? CALIBRES_MEDIA_VALVA
        : CALIBRES_TALLO_SOLO;
      const productos = productosAgrupados[presentacion];
      // Filtrar solo calibres que tienen datos
      const calibresConDatos = calibresBase.filter(calibre =>
        productos.some(p => p.codigo === calibre && p.cantidad > 0)
      );
      // Altura = header(8) + columnas(6) + calibres * 6 + total(6)
      const tableHeight = 8 + 6 + calibresConDatos.length * 6 + 6;
      return { presentacion, calibres: calibresConDatos, tableHeight };
    });

    // Calcular ancho de tablas
    const tableWidth = (pageWidth - margin * 2 - 10) / 2; // 2 tablas por fila

    // Renderizar tablas con manejo inteligente de páginas
    let tableX = margin;
    let tableY = yPos;
    let maxYInRow = yPos;
    let tablesInCurrentRow = 0;
    const tablesPerRow = 2;

    tableInfos.forEach((tableInfo) => {
      const { presentacion, calibres, tableHeight } = tableInfo;
      const productos = productosAgrupados[presentacion];

      // Verificar si necesitamos nueva fila
      if (tablesInCurrentRow >= tablesPerRow) {
        tableX = margin;
        tableY = maxYInRow + 5;
        tablesInCurrentRow = 0;
      }

      // Verificar si la tabla cabe en la página actual
      if (tableY + tableHeight > pageHeight - FOOTER_HEIGHT) {
        // Nueva página
        doc.addPage();
        tableY = margin;
        tableX = margin;
        maxYInRow = margin;
        tablesInCurrentRow = 0;
      }

      // Crear tabla
      const tableEndY = renderProductTable(doc, tableX, tableY, tableWidth, presentacion, productos, calibres);

      maxYInRow = Math.max(maxYInRow, tableEndY);
      tableX += tableWidth + 5;
      tablesInCurrentRow++;
    });

    yPos = maxYInRow + 10;

    // ==========================================
    // INFORMACIÓN DEL CLIENTE Y TOTALES
    // ==========================================

    // Calcular espacio necesario para el footer
    const footerContentHeight = 35 + (data.observaciones ? 20 : 0);

    // Verificar si necesitamos nueva página para el footer
    if (yPos + footerContentHeight > pageHeight - 15) {
      doc.addPage();
      yPos = margin;
    }

    // Cliente info
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENTE:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.clienteRazonSocial || 'N/A', margin + 25, yPos);

    // Totales (derecha)
    const totalGeneralKg = calcularTotalGeneral(data.productosTerminados, data.productosTipoB || []);
    // Rendimiento = Total Productos Terminados / Peso Materia Prima
    const pesoMateriaPrima = calcularPesoMateriaPrima(data.materiasPrimas || []);
    const rendimiento = pesoMateriaPrima > 0 ? (totalGeneralKg / pesoMateriaPrima).toFixed(2) : '0';

    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL GENERAL', pageWidth - 80, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(`${totalGeneralKg.toFixed(1)} Kg.`, pageWidth - 40, yPos);

    yPos += 6;

    doc.setFont('helvetica', 'bold');
    doc.text('PLACA VEHICULO:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.placaVehiculo || 'N/A', margin + 40, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('N° GUÍA REMISIÓN:', margin + 75, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.numeroGuiaRemision || 'N/A', margin + 115, yPos);

    doc.setFont('helvetica', 'bold');
    doc.text('RENDIMIENTO (KG/MALLA)', pageWidth - 80, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(rendimiento, pageWidth - 25, yPos);

    yPos += 10;

    // Observaciones
    if (data.observaciones) {
      // Verificar espacio para observaciones
      if (yPos + 25 > pageHeight - 30) {
        doc.addPage();
        yPos = margin;
      }
      doc.setFont('helvetica', 'bold');
      doc.text('OBSERVACIONES:', margin, yPos);
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const obsLines = doc.splitTextToSize(data.observaciones, pageWidth - margin * 2);
      doc.text(obsLines, margin, yPos);
      yPos += obsLines.length * 4 + 5;
    }

    // Firma - siempre al final de la página
    const firmaY = Math.max(yPos + 10, pageHeight - 25);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.line(pageWidth / 2 - 30, firmaY, pageWidth / 2 + 30, firmaY);
    doc.text('JEFE DE PRODUCCION', pageWidth / 2, firmaY + 5, { align: 'center' });

    // ==========================================
    // ABRIR PDF
    // ==========================================
    const fileName = `Parte_Produccion_${data.numeroComprobante}_${formatDateForFileName(data.fecha)}.pdf`;
    doc.save(fileName);
  }
};

/**
 * Renderiza una tabla de productos
 */
function renderProductTable(
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  titulo: string,
  productos: CalibreGroup[],
  calibres: string[]
): number {
  const cellHeight = 6;
  const headerHeight = 8;
  let currentY = y;

  // Título de la tabla
  doc.setFillColor(240, 240, 240);
  doc.rect(x, currentY, width, headerHeight, 'F');
  doc.setDrawColor(0);
  doc.rect(x, currentY, width, headerHeight, 'S');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(titulo.toUpperCase(), x + width / 2, currentY + 5.5, { align: 'center' });
  currentY += headerHeight;

  // Headers de columnas
  const colWidth = width / 3;
  doc.setFillColor(250, 250, 250);
  doc.rect(x, currentY, width, cellHeight, 'F');
  doc.rect(x, currentY, width, cellHeight, 'S');

  doc.setFontSize(7);
  doc.text('CODIGO', x + colWidth / 2, currentY + 4, { align: 'center' });
  doc.line(x + colWidth, currentY, x + colWidth, currentY + cellHeight);
  doc.text('CANTIDAD', x + colWidth + colWidth / 2, currentY + 4, { align: 'center' });
  doc.line(x + colWidth * 2, currentY, x + colWidth * 2, currentY + cellHeight);
  doc.text('U.M.', x + colWidth * 2 + colWidth / 2, currentY + 4, { align: 'center' });
  currentY += cellHeight;

  // Filas de datos - solo mostrar calibres que tienen datos
  doc.setFont('helvetica', 'normal');
  let total = 0;

  // Filtrar calibres que tienen datos y ordenarlos
  const calibresConDatos = calibres
    .map(calibre => {
      const producto = productos.find(p => p.codigo === calibre);
      return { calibre, cantidad: producto?.cantidad || 0 };
    })
    .filter(item => item.cantidad > 0);

  calibresConDatos.forEach(({ calibre, cantidad }) => {
    total += cantidad;

    doc.rect(x, currentY, width, cellHeight, 'S');
    doc.text(calibre, x + colWidth / 2, currentY + 4, { align: 'center' });
    doc.line(x + colWidth, currentY, x + colWidth, currentY + cellHeight);

    doc.text(cantidad.toFixed(1), x + colWidth + colWidth / 2, currentY + 4, { align: 'center' });
    doc.line(x + colWidth * 2, currentY, x + colWidth * 2, currentY + cellHeight);
    doc.text('KG.', x + colWidth * 2 + colWidth / 2, currentY + 4, { align: 'center' });
    currentY += cellHeight;
  });

  // Fila de TOTAL
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(230, 230, 230);
  doc.rect(x, currentY, width, cellHeight, 'F');
  doc.rect(x, currentY, width, cellHeight, 'S');
  doc.text('TOTAL', x + colWidth / 2, currentY + 4, { align: 'center' });
  doc.line(x + colWidth, currentY, x + colWidth, currentY + cellHeight);
  doc.text(total.toFixed(1), x + colWidth + colWidth / 2, currentY + 4, { align: 'center' });
  doc.line(x + colWidth * 2, currentY, x + colWidth * 2, currentY + cellHeight);
  doc.text('KG.', x + colWidth * 2 + colWidth / 2, currentY + 4, { align: 'center' });
  currentY += cellHeight;

  return currentY;
}

/**
 * Agrupa productos por presentación (Tallo Solo, Media Valva, etc.)
 * Considera las devoluciones internas al calcular los totales
 */
function agruparProductosPorPresentacion(
  productosTerminados: GrupoProductoTerminado[],
  productosTipoB: ProductoTerminadoTipoB[]
): Record<string, CalibreGroup[]> {
  const grupos: Record<string, CalibreGroup[]> = {};

  // Procesar productos tipo A
  productosTerminados.forEach(grupo => {
    grupo.items.forEach(item => {
      // Extraer presentación y calibre del artículo
      const { presentacion, calibre } = parseArticulo(item.articulo || '');

      if (!grupos[presentacion]) {
        grupos[presentacion] = [];
      }

      // Calcular la cantidad efectiva considerando devoluciones internas
      const cantidadDevInt = item.devolucionInterna ? (item.cantidadDevolucionInterna || 0) : 0;
      const cantidadEfectiva = item.cantidad - cantidadDevInt;
      const totalEfectivo = cantidadEfectiva * item.pesoEmpaqueInterior;

      const existente = grupos[presentacion].find(g => g.codigo === calibre);
      if (existente) {
        existente.cantidad += totalEfectivo;
      } else {
        grupos[presentacion].push({ codigo: calibre, cantidad: totalEfectivo });
      }
    });
  });

  // Procesar productos tipo B (Media Valva generalmente)
  productosTipoB.forEach(item => {
    const { presentacion, calibre } = parseArticulo(item.producto || '');
    const presentacionKey = presentacion.includes('MEDIA VALVA')
      ? `MEDIA VALVA - ${presentacion.replace('MEDIA VALVA', '').trim() || 'TALLO SOLO'}`
      : presentacion;

    if (!grupos[presentacionKey]) {
      grupos[presentacionKey] = [];
    }

    const existente = grupos[presentacionKey].find(g => g.codigo === calibre);
    if (existente) {
      existente.cantidad += item.total;
    } else {
      grupos[presentacionKey].push({ codigo: calibre, cantidad: item.total });
    }
  });

  return grupos;
}

/**
 * Extrae presentación y calibre del nombre del artículo
 */
function parseArticulo(articulo: string): { presentacion: string; calibre: string } {
  // Patrones comunes: "TALLO SOLO 30-40", "MEDIA VALVA TALLO CORAL 20-30"
  const calibreMatch = articulo.match(/(\d+[-]\d+|\d+[-]over)/i);
  const calibre = calibreMatch ? calibreMatch[1] : 'OTROS';

  let presentacion = 'TALLO SOLO';
  if (articulo.toUpperCase().includes('MEDIA VALVA')) {
    if (articulo.toUpperCase().includes('TALLO CORAL')) {
      presentacion = 'MEDIA VALVA - TALLO CORAL';
    } else {
      presentacion = 'MEDIA VALVA - TALLO SOLO';
    }
  } else if (articulo.toUpperCase().includes('TALLO CORAL')) {
    presentacion = 'TALLO CORAL';
  }

  return { presentacion, calibre };
}

/**
 * Calcula el total general de productos
 * Considera las devoluciones internas al calcular los totales
 */
function calcularTotalGeneral(
  productosTerminados: GrupoProductoTerminado[],
  productosTipoB: ProductoTerminadoTipoB[]
): number {
  let total = 0;

  // Calcular total de productos A considerando devoluciones internas
  productosTerminados.forEach(grupo => {
    grupo.items.forEach(item => {
      const cantidadDevInt = item.devolucionInterna ? (item.cantidadDevolucionInterna || 0) : 0;
      const cantidadEfectiva = item.cantidad - cantidadDevInt;
      total += cantidadEfectiva * item.pesoEmpaqueInterior;
    });
  });

  productosTipoB.forEach(item => {
    total += item.total;
  });

  return total;
}

/**
 * Calcula el peso total de materias primas
 */
function calcularPesoMateriaPrima(materiasPrimas: GrupoMateriaPrima[]): number {
  let total = 0;

  materiasPrimas.forEach(grupo => {
    total += grupo.totales.peso;
  });

  return total;
}

/**
 * Formatea fecha para mostrar
 */
function formatDate(dateString: string): string {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
}

/**
 * Formatea fecha para nombre de archivo
 */
function formatDateForFileName(dateString: string): string {
  if (!dateString) return 'fecha';
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch {
    return 'fecha';
  }
}

export default parteProduccionPdfService;
